import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Card, Button, Modal, Table, Loading, Badge, Alert, Input } from '../components/common'
import api from '../services/api'

const ALL_PERMISSIONS = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/', desc: 'Lihat ringkasan statistik SPK, progress produksi, dan shortcut menu utama' },
  { id: 'station_update', label: 'Update Stasiun', icon: '📱', path: '/station', desc: 'Update progress produksi per stasiun, input QC, dan kelola operator' },
  { id: 'cetak_comcolor', label: 'Cetak Comcolor', icon: '⚡', path: '/comcolor', desc: 'Operator Comcolor — download file, cetak, input hasil. Langsung siap binding (skip susun)' },
  { id: 'edit_qc', label: 'Edit Riwayat QC', icon: '✏️', path: '/edit-qc', desc: 'Edit data QC yang sudah diinput (susun, binding, potong sisir, laminasi)' },
  { id: 'spk', label: 'Manajemen SPK', icon: '📝', path: '/spk', desc: 'Buat, edit, hapus SPK, dan selesaikan SPK yang sudah masuk gudang' },
  { id: 'spk_management', label: 'Master Buku', icon: '📚', path: '/books', desc: 'Kelola data buku (judul, kode, mata pelajaran, spesifikasi cetak)' },
  { id: 'review_file', label: 'Review File', icon: '🔎', path: '/reviewer', desc: 'Validasi atau tolak file desain yang diupload, lihat rekap status file' },
  { id: 'files', label: 'Upload File', icon: '📤', path: '/upload-file', desc: 'Upload file desain (ISI, Cover, Cover Custom, Sisipan) untuk SPK' },
  { id: 'master_sekolah', label: 'Master Sekolah', icon: '🏫', path: '/schools', desc: 'Kelola data sekolah untuk cover custom dan sisipan' },
  { id: 'vendor', label: 'Manajemen Vendor', icon: '🏭', path: '/vendors', desc: 'Kelola data vendor cetakan luar/outsource' },
  { id: 'queue', label: 'Antrian Produksi', icon: '📋', path: '/queue', desc: 'Lihat urutan antrian SPK yang siap masuk produksi' },
  { id: 'reports', label: 'Laporan', icon: '📈', path: '/reports', desc: 'Akses laporan: Status SPK, Rekap Operator, Sekolah Custom, Cetakan Vendor' },
  { id: 'history', label: 'Riwayat Aktivitas', icon: '📜', desc: 'Lihat log aktivitas semua user di sistem (siapa melakukan apa)' },
  { id: 'warehouse', label: 'Gudang', icon: '📦', path: '/warehouse', desc: 'Input stok masuk gudang, kelola rak penyimpanan' },
  { id: 'users', label: 'User Management', icon: '👥', path: '/users', desc: 'Tambah, edit, hapus akun pengguna sistem' },
  { id: 'roles', label: 'Hak Akses', icon: '🔐', path: '/roles', desc: 'Atur role dan permission untuk setiap jenis pengguna' },
  { id: 'api_management', label: 'API Eksternal', icon: '🔗', path: '/api-management', desc: 'Kelola API key untuk integrasi sistem luar (cek stok, webhook, dll)' },
]

const ALL_STATIONS = [
  { id: 'validasi', label: 'Validasi', desc: 'Cek kelengkapan file sebelum masuk cetak', group: 'Pra-Cetak' },
  { id: 'cetak_isi', label: 'Cetak ISI', desc: 'Input info cetak isi (vendor, mode cetak, tanggal mulai)', group: 'Cetak' },
  { id: 'potong_isi', label: 'Potong ISI', desc: 'QC hasil cetak isi sebelum lanjut ke susun', group: 'Cetak' },
  { id: 'susun', label: 'Susun / Batching', desc: 'Pilih peruntukan (reguler/custom) dan buat batch produksi', group: 'Cetak' },
  { id: 'cetak_cover', label: 'Cetak Cover', desc: 'Input info cetak cover reguler dan custom', group: 'Cover' },
  { id: 'qc_cover', label: 'QC Cover', desc: 'QC hasil cetak cover sebelum laminasi', group: 'Cover' },
  { id: 'laminasi', label: 'Laminasi', desc: 'Proses laminasi cover, input data operator', group: 'Cover' },
  { id: 'cetak_sisipan', label: 'Cetak Sisipan', desc: 'Input info cetak sisipan untuk sekolah custom', group: 'Cover' },
  { id: 'qc_sisipan', label: 'QC Sisipan', desc: 'QC hasil cetak sisipan', group: 'Cover' },
  { id: 'binding', label: 'Binding', desc: 'QC binding (jilid) per batch, input jumlah reject', group: 'Finishing' },
  { id: 'qc_binding', label: 'QC Binding', desc: 'Verifikasi hasil binding sebelum potong sisir', group: 'Finishing' },
  { id: 'potong_sisir', label: 'Potong Sisir', desc: 'QC potong sisir per batch', group: 'Finishing' },
  { id: 'qc_potong', label: 'QC Potong', desc: 'Verifikasi hasil potong sisir sebelum masuk gudang', group: 'Finishing' },
  { id: 'gudang', label: 'Input Gudang', desc: 'Catat buku masuk gudang, pilih rak penyimpanan', group: 'Finishing' },
]

function RolesPage() {
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [stations, setStations] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [newRole, setNewRole] = useState({ role_key: '', name: '' })

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await api.get('/roles')
      if (response.data.success) setRoles(response.data.data.roles || [])
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const handleEdit = (role) => {
    setEditing(role)
    setPermissions(role.permissions || [])
    setStations(role.stations || [])
    setError('')
    setShowModal(true)
  }

  const handleSubmit = async () => {
    setSaving(true)
    try {
      const response = await api.put(`/roles/${editing.role_key}`, {
        name: editing.name,
        permissions,
        stations
      })
      if (response.data.success) { setShowModal(false); fetchData() }
      else { setError(response.data.message) }
    } catch (err) { setError('Gagal menyimpan') }
    finally { setSaving(false) }
  }

  const handleAddRole = async () => {
    if (!newRole.role_key || !newRole.name) {
      setError('Role key dan nama wajib diisi')
      return
    }
    if (!/^[a-z_]+$/.test(newRole.role_key)) {
      setError('Role key harus lowercase dan tanpa spasi (gunakan underscore)')
      return
    }

    setSaving(true)
    try {
      const response = await api.post('/roles', {
        role_key: newRole.role_key,
        name: newRole.name,
        permissions: ['dashboard'],
        stations: []
      })
      if (response.data.success) { 
        setShowAddModal(false)
        setNewRole({ role_key: '', name: '' })
        setError('')
        fetchData()
      } else { 
        setError(response.data.message) 
      }
    } catch (err) { 
      setError(err.response?.data?.message || 'Gagal menambah role') 
    } finally { 
      setSaving(false) 
    }
  }

  const handleDeleteRole = async (role) => {
    if (role.role_key === 'admin') {
      return alert('Role admin tidak dapat dihapus')
    }
    if (!window.confirm(`Hapus role "${role.name}"?\n\nPastikan tidak ada user yang menggunakan role ini.`)) return
    
    try {
      const response = await api.delete(`/roles/${role.role_key}`)
      if (response.data.success) {
        fetchData()
      } else {
        alert(response.data.message || 'Gagal menghapus role')
      }
    } catch (err) { 
      alert(err.response?.data?.message || 'Gagal menghapus role')
    }
  }

  const togglePermission = (id) => {
    setPermissions(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id])
  }

  const toggleStation = (id) => {
    setStations(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id])
  }

  const columns = [
    { header: 'Role Key', render: (row) => <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{row.role_key}</span> },
    { header: 'Nama', accessor: 'name' },
    { header: 'Permissions', render: (row) => (
      <div className="flex flex-wrap gap-1">
        {(row.permissions || []).includes('all') ? (
          <Badge variant="success" size="sm">Full Access</Badge>
        ) : (
          <>
            {(row.permissions || []).slice(0, 3).map(p => <Badge key={p} variant="primary" size="sm">{p}</Badge>)}
            {(row.permissions || []).length > 3 && <Badge variant="default" size="sm">+{row.permissions.length - 3}</Badge>}
          </>
        )}
      </div>
    )},
    { header: 'Stations', render: (row) => (
      <div className="flex flex-wrap gap-1">
        {(row.stations || []).slice(0, 3).map(s => <Badge key={s} variant="info" size="sm">{s}</Badge>)}
        {(row.stations || []).length > 3 && <Badge variant="default" size="sm">+{row.stations.length - 3}</Badge>}
      </div>
    )},
    { header: 'Aksi', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleEdit(row)} className="text-blue-600 hover:underline text-sm">Edit</button>
        {row.role_key !== 'admin' && (
          <button onClick={() => handleDeleteRole(row)} className="text-red-600 hover:underline text-sm">Hapus</button>
        )}
      </div>
    )}
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">🔐 Manajemen Hak Akses</h1>
          <p className="text-gray-500">Konfigurasi permissions dan akses stasiun untuk setiap role</p>
        </div>
        <Button onClick={() => { setNewRole({ role_key: '', name: '' }); setError(''); setShowAddModal(true) }}>
          ➕ Tambah Role
        </Button>
      </div>

      <Card>{loading ? <Loading /> : <Table columns={columns} data={roles} emptyText="Belum ada role" />}</Card>

      {/* Add Role Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Tambah Role Baru" size="md"
        footer={<><Button variant="secondary" onClick={() => setShowAddModal(false)}>Batal</Button><Button onClick={handleAddRole} loading={saving}>Simpan</Button></>}>
        {error && <Alert type="error" className="mb-4" onClose={() => setError('')}>{error}</Alert>}
        <div className="space-y-4">
          <Input label="Role Key *" value={newRole.role_key} 
            onChange={(e) => setNewRole(prev => ({ ...prev, role_key: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
            placeholder="Contoh: operator_cetak" />
          <p className="text-xs text-gray-500 -mt-2">Huruf kecil tanpa spasi, gunakan underscore</p>
          <Input label="Nama Role *" value={newRole.name} 
            onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Contoh: Operator Cetak" />
          <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
            💡 Setelah role dibuat, edit untuk mengatur permissions dan akses stasiun.
          </div>
        </div>
      </Modal>

      {/* Edit Role Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={`Edit Role: ${editing?.name}`} size="xl"
        footer={<><Button variant="secondary" onClick={() => setShowModal(false)}>Batal</Button><Button onClick={handleSubmit} loading={saving}>Simpan</Button></>}>
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">📋 Permissions</h4>
              <div className="flex gap-2">
                <button onClick={() => setPermissions(ALL_PERMISSIONS.map(p => p.id))} className="text-xs text-blue-600 hover:underline">Semua</button>
                <button onClick={() => setPermissions([])} className="text-xs text-gray-500 hover:underline">Hapus</button>
              </div>
            </div>
            <div className="space-y-1 max-h-[28rem] overflow-y-auto border rounded-lg p-2">
              {ALL_PERMISSIONS.map(p => (
                <label key={p.id} className={`flex items-start gap-3 p-2 rounded cursor-pointer transition-colors ${permissions.includes(p.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <input type="checkbox" checked={permissions.includes(p.id)} onChange={() => togglePermission(p.id)} className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{p.icon}</span>
                      <span className="font-medium text-sm">{p.label}</span>
                      {p.path && (
                        <Link to={p.path} onClick={(e) => e.stopPropagation()} className="text-blue-500 hover:text-blue-700 text-[10px] flex-shrink-0" title={`Buka halaman ${p.label}`}>
                          buka &rarr;
                        </Link>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug">{p.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">🏭 Akses Stasiun</h4>
              <div className="flex gap-2">
                <button onClick={() => setStations(ALL_STATIONS.map(s => s.id))} className="text-xs text-blue-600 hover:underline">Semua</button>
                <button onClick={() => setStations([])} className="text-xs text-gray-500 hover:underline">Hapus</button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-2">Menentukan stasiun mana yang bisa diakses di halaman <Link to="/station" className="text-blue-500 hover:text-blue-700">Update Stasiun &rarr;</Link></p>
            <div className="max-h-[28rem] overflow-y-auto border rounded-lg p-2">
              {['Pra-Cetak', 'Cetak', 'Cover', 'Finishing'].map(group => (
                <div key={group} className="mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold px-2 py-1 border-b mb-1">{group}</div>
                  <div className="space-y-1">
                    {ALL_STATIONS.filter(s => s.group === group).map(s => (
                      <label key={s.id} className={`flex items-start gap-3 p-2 rounded cursor-pointer transition-colors ${stations.includes(s.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                        <input type="checkbox" checked={stations.includes(s.id)} onChange={() => toggleStation(s.id)} className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="font-medium text-sm">{s.label}</span>
                          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{s.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default RolesPage
